/**
 * StudioCanvas – Phase 4 Workspace Studio.
 *
 * Mounts the SAME launcher rendering engine (hooks + components) inside the
 * Workspace Studio shell.  This is NOT a fork of the renderer — it is the
 * same hooks and JSX used by HomeLauncherPage, wired to a workspace draft
 * runtimeContext instead of a legacy load.
 *
 * Shared components: IconGrid, AppIcon, ButtonAdminModal, SipPagingDialog,
 *   RtpPagingDialog, ImageDisplayModal (all from HomeLauncher).
 * Shared hooks: useLauncherLayout, useLauncherDnD, useLauncherEditMode,
 *   useLauncherResponsive, useLauncherPersistence, useLauncherActions.
 *
 * Props:
 *  - runtimeContext   RuntimeContext (runtimeMode='draft-edit')
 *  - initialIcons     icon[] pre-loaded from draft/published
 *  - rawLayoutData    full normalized layout (theme, grid, etc.)
 *  - uid              from auth
 *  - tenantId         from auth
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate }       from 'react-router-dom';
import { Box, Button, CircularProgress, Fab, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { useIncident }          from '../../../contexts/IncidentContext';
import { useAuth }              from '../../../contexts/AuthContext';
import IconGrid                 from '../../HomeLauncher/components/IconGrid';
import ButtonAdminModal         from '../../HomeLauncher/components/ButtonAdminModal';
import SipPagingDialog          from '../../HomeLauncher/components/SipPagingDialog';
import RtpPagingDialog          from '../../HomeLauncher/components/RtpPagingDialog';
import RtpPagingAdvancedDialog  from '../../HomeLauncher/components/RtpPagingAdvancedDialog';
import ImageDisplayModal        from '../../../components/actions/ImageDisplayModal';
import WallpaperIcon            from '@mui/icons-material/Wallpaper';
import DeleteOutlineIcon        from '@mui/icons-material/DeleteOutline';
import CallPanel                from '../../HomeLauncher/components/CallPanel';
import CollaborationWorkspaceModal from '../../../components/collaboration/CollaborationWorkspaceModal';
import ContactListViewerDialog from '../../../components/contactList/ContactListViewerDialog';
import ContactListCallDialog from '../../../components/contactList/ContactListCallDialog';
import PhoneOverlay from '../../../components/phone/PhoneOverlay';
import RemindersOverlay from '../../../components/reminders/RemindersOverlay';
import EmailRemOverlay from '../../../components/emailRem/EmailRemOverlay';
import useLauncherEditMode      from '../../../hooks/homeLauncher/useLauncherEditMode';
import {
  uploadWorkspaceBackground,
  removeWorkspaceBackground,
  studioBackgroundWritesLocked,
  commitBackgroundChange,
  parseManagedBackground,
} from '../../../services/workspaceRuntime/workspaceBackgroundService';
import { flushDraftSave } from '../../../services/workspaceRuntime/workspaceDraftService';
import useResolvedBackgroundUrl from '../../../services/workspaceRuntime/useResolvedBackgroundUrl';
import useLauncherResponsive    from '../../../hooks/homeLauncher/useLauncherResponsive';
import useLauncherPersistence   from '../../../hooks/homeLauncher/useLauncherPersistence';
import useLauncherLayout        from '../../../hooks/homeLauncher/useLauncherLayout';
import useLauncherDnD           from '../../../hooks/homeLauncher/useLauncherDnD';
import useLauncherActions       from '../../../hooks/homeLauncher/useLauncherActions';
import { LAUNCHER_GRID }        from '../../../utils/launcherGrid';

export default function StudioCanvas({
  runtimeContext,
  initialIcons,
  rawLayoutData,
  uid,
  tenantId,
  onLayoutSnapshot,
}) {
  const { openIncidentById } = useIncident();
  const { userData, role, userClaims } = useAuth();
  const resolvedRole = role || userClaims?.role || userData?.role;
  const navigate             = useNavigate();

  const bgInputRef    = useRef(null);
  const [bgUploading, setBgUploading] = useState(false);
  const [bgError,     setBgError]     = useState('');
  const backgroundLocked = studioBackgroundWritesLocked();

  const em          = useLauncherEditMode();
  const responsive  = useLauncherResponsive();
  const resolvedBackground = useResolvedBackgroundUrl(responsive.backgroundImageUrl);

  // Boot persistence in draft-edit mode from the start
  const persistence = useLauncherPersistence({ initialRuntimeContext: runtimeContext });

  const layout = useLauncherLayout({
    gridMax:             responsive.GRID_MAX,
    uid,
    saveLayout:          persistence.saveLayout,
    getResponsiveConfig: responsive.getConfig,
  });

  const dnd = useLauncherDnD({
    icons:            layout.icons,
    setIcons:         layout.setIcons,
    editMode:         em.editMode,
    resetEditTimeout: em.resetEditTimeout,
    gridMax:          responsive.GRID_MAX,
    handleReorder:    layout.handleReorder,
  });

  const actions = useLauncherActions({
    editMode:         em.editMode,
    tenantId,
    openIncidentById,
    navigate,
    userData,
    role: resolvedRole,
  });

  // Sync runtimeContext when it changes (e.g., after publish reloads workspace)
  useEffect(() => {
    if (runtimeContext?.workspaceId) {
      persistence.setRuntimeContext(runtimeContext);
    }
  }, [runtimeContext?.workspaceId, runtimeContext?.workspaceVersionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize layout from props (draft/published data)
  useEffect(() => {
    if (!initialIcons) return;
    layout.applyLayoutData(initialIcons);
    if (rawLayoutData) responsive.applyLayoutData(rawLayoutData);
    persistence.setLayoutLoaded(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps — intentional: only on mount

  useEffect(() => {
    onLayoutSnapshot?.({
      icons: layout.icons,
      ...responsive.getConfig(),
    });
  }, [
    layout.icons,
    onLayoutSnapshot,
    responsive,
  ]);

  const handleAddIcon = useCallback(() => {
    const newIcon = layout.handleAddIcon();
    if (newIcon) em.setEditingIcon(newIcon);
  }, [layout.handleAddIcon, em.setEditingIcon]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveIcon = useCallback((changes) => {
    if (!em.editingIcon) return;
    layout.handleSaveIcon(em.editingIcon.id, changes);
    em.setEditingIcon(null);
  }, [em.editingIcon, layout.handleSaveIcon, em.setEditingIcon]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteById = useCallback((id) => {
    layout.handleDeleteById(id);
    em.setEditingIcon(null);
  }, [layout.handleDeleteById, em.setEditingIcon]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: LAUNCHER_GRID.MIN_PAGE_WIDTH, minHeight: LAUNCHER_GRID.MIN_PAGE_HEIGHT, overflow: 'auto' }}>

      {/* Studio editing toolbar — replaces long-press discovery */}
      <Box
        sx={{
          px: 2, py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          bgcolor: 'background.paper',
        }}
      >
        {em.editMode ? (
          <Button size="small" variant="contained" onClick={() => em.setEditMode(false)}>
            Done Editing
          </Button>
        ) : (
          <Button size="small" variant="outlined" onClick={() => em.setEditMode(true)}>
            Edit Layout
          </Button>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {em.editMode
            ? 'Drag icons to reorder · Click edit icon to modify · Long-press also works'
            : 'Enter edit mode to rearrange, add, or delete buttons'}
        </Typography>

        {/* Background image controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          {resolvedBackground && (
            <Box
              component="img"
              src={resolvedBackground}
              alt="background preview"
              sx={{ width: 40, height: 28, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
            />
          )}
          <Tooltip title={backgroundLocked ? 'View access cannot change the background' : (responsive.backgroundImageUrl ? 'Change background image' : 'Set background image')}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={bgUploading ? <CircularProgress size={14} /> : <WallpaperIcon fontSize="small" />}
                disabled={bgUploading || backgroundLocked}
                onClick={() => { setBgError(''); bgInputRef.current?.click(); }}
              >
                {responsive.backgroundImageUrl ? 'Change' : 'Background'}
              </Button>
            </span>
          </Tooltip>
          {responsive.backgroundImageUrl && (
            <Tooltip title="Remove background image">
              <IconButton
                size="small"
                color="error"
                disabled={backgroundLocked}
                onClick={async () => {
                  const previous = responsive.backgroundImageUrl;
                  setBgError('');
                  const result = await commitBackgroundChange({
                    previous,
                    upload: async () => '',
                    save: () => flushDraftSave(runtimeContext, {
                      icons: layout.icons,
                      ...responsive.getConfig(),
                      backgroundImageUrl: '',
                    }, { strict: true }),
                  });
                  responsive.setBackgroundImageUrl(result.saved ? '' : previous);
                  if (!result.saved) {
                    setBgError(result.message);
                    return;
                  }
                  if (parseManagedBackground(previous)) removeWorkspaceBackground(previous);
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {bgError && (
            <Typography variant="caption" color="error">{bgError}</Typography>
          )}
        </Box>

        {/* Hidden file input */}
        <input
          ref={bgInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          style={{ display: 'none' }}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            setBgUploading(true);
            setBgError('');
            const previous = responsive.backgroundImageUrl;
            try {
              const result = await commitBackgroundChange({
                previous,
                upload: () => uploadWorkspaceBackground(tenantId, runtimeContext.workspaceId, file),
                save: (reference) => flushDraftSave(runtimeContext, {
                  icons: layout.icons,
                  ...responsive.getConfig(),
                  backgroundImageUrl: reference,
                }, { strict: true }),
              });
              responsive.setBackgroundImageUrl(result.backgroundImageUrl);
              if (!result.saved) setBgError(result.message);
              else if (parseManagedBackground(previous)) removeWorkspaceBackground(previous);
            } finally {
              setBgUploading(false);
            }
          }}
        />
      </Box>

      {/* Canvas — same rendering as HomeLauncherPage */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          minWidth: 0,
          ...(resolvedBackground ? {
            backgroundImage:    `url(${resolvedBackground})`,
            backgroundSize:     'cover',
            backgroundPosition: 'center',
            backgroundRepeat:   'no-repeat',
          } : {
            bgcolor: responsive.isDark ? 'grey.900' : 'grey.50',
          }),
        }}
      >
        <DndContext
          sensors={dnd.sensors}
          collisionDetection={closestCenter}
          onDragStart={dnd.onDragStart}
          onDragMove={dnd.onDragMove}
          onDragEnd={dnd.onDragEnd}
          onDragCancel={dnd.onDragCancel}
        >
          <Box sx={{ height: '100%' }}>
            <IconGrid
              slots={layout.slots}
              editMode={em.editMode}
              onLongPress={() => em.setEditMode(true)}
              onIconClick={actions.handleIconPress}
              onEditRequest={(icon) => em.setEditingIcon(icon)}
              onDeleteRequest={handleDeleteById}
              onAddRequest={handleAddIcon}
              canAdd={layout.icons.length < responsive.GRID_MAX}
              gridMax={responsive.GRID_MAX}
              dark={responsive.isDark}
              columns={responsive.gridColumns}
              iconSize={responsive.iconSize}
              gap={responsive.gap}
              showGrid={responsive.showGrid}
              rows={responsive.gridRows}
              tenantId={tenantId}
            />
          </Box>
        </DndContext>
      </Box>

      {/* Add-icon FAB */}
      {em.editMode && layout.icons.length < responsive.GRID_MAX && (
        <Fab
          color="primary"
          onClick={handleAddIcon}
          size="medium"
          sx={{ position: 'absolute', right: 24, bottom: 96, zIndex: 1200 }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Shared modals — identical to HomeLauncherPage */}
      {em.editingIcon && (
        <ButtonAdminModal
          open={Boolean(em.editingIcon)}
          icon={em.editingIcon}
          imageScope="shared"
          onSave={handleSaveIcon}
          onClose={() => em.setEditingIcon(null)}
          onDelete={() => handleDeleteById(em.editingIcon.id)}
          tenantId={tenantId}
        />
      )}

      <RtpPagingDialog
        open={actions.rtpDialogOpen}
        onClose={() => { actions.setRtpDialogOpen(false); actions.setActivePagingIcon(null); }}
        presetZoneId={actions.activePagingIcon?.pagingZoneId || null}
        presetZoneName={actions.activePagingIcon?.label || null}
        micDeviceId={actions.activePagingIcon?.micDeviceId || null}
      />

      <RtpPagingAdvancedDialog
        open={actions.rtpManualDialogOpen}
        onClose={() => actions.setRtpManualDialogOpen(false)}
      />

      <SipPagingDialog
        open={actions.sipDialogOpen}
        onClose={() => { actions.setSipDialogOpen(false); actions.setActiveSipPagingIcon(null); actions.setActiveSipUri(null); }}
        presetZoneId={actions.activeSipPagingIcon?.sipPagingZoneId || null}
        presetZoneName={actions.activeSipPagingIcon?.label || null}
        sipUri={actions.activeSipUri}
      />

      <ImageDisplayModal
        open={actions.imageDisplayDialogOpen}
        onClose={() => { actions.setImageDisplayDialogOpen(false); actions.setActiveImageDisplayIcon(null); }}
        actionLabel={actions.activeImageDisplayIcon?.label || 'Image Display'}
        loading={actions.imageDisplayLoading}
        onSubmit={actions.handleImageDisplaySubmit}
      />

      <CallPanel tenantId={tenantId} />

      <CollaborationWorkspaceModal
        open={actions.collaborationModalOpen}
        onClose={() => {
          actions.setCollaborationModalOpen(false);
          actions.setActiveCollaborationData(null);
        }}
        collaborationData={actions.activeCollaborationData}
      />

      <ContactListViewerDialog
        open={actions.contactListDialogOpen}
        onClose={() => {
          actions.setContactListDialogOpen(false);
          actions.setActiveContactListId(null);
        }}
        contactListId={actions.activeContactListId}
        tenantId={tenantId}
      />

      <ContactListCallDialog
        open={actions.contactListCallOpen}
        onClose={() => {
          actions.setContactListCallOpen(false);
          actions.setActiveContactListCall(null);
        }}
        contactList={actions.activeContactListCall?.contactList || null}
        tenantId={tenantId}
        sessionId={actions.activeContactListCall?.sessionId || ''}
      />

      <PhoneOverlay
        open={actions.phoneOpen}
        onClose={() => actions.setPhoneOpen(false)}
        userData={userData}
        role={resolvedRole}
      />

      <RemindersOverlay
        open={actions.remindersOpen}
        onClose={() => actions.setRemindersOpen(false)}
        userData={userData}
        tenantId={tenantId}
      />

      <EmailRemOverlay
        open={actions.emailRemOpen}
        onClose={() => actions.setEmailRemOpen(false)}
        userData={userData}
      />
    </Box>
  );
}
